import { inject } from '@angular/core';
import { ActivityEntityName, EntityViewDisplay, JobTemplateQuestion, JobTemplateQuestionCommandId, type MetadataEntityData, type MetadataEntityFieldData } from '@jms/model';
import { EntityDataService } from '@workbuddy/services/entities';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { resolveBoardParams, resolveBoardRowId, type EntityBoardContext, type EntityBoardReorderConfig } from '@workbuddy/ui/v2/blocks/entity.board';
import { QuestionShowConditionCellRenderer } from './board/cell.renderers/show.condition/question.show.condition.cell.renderer';
import { buildJobTemplateQuestionRowCommands } from './board/job.template.question.row.commands';
import { JOB_TEMPLATE_QUESTION_SCOPE, type JobTemplateQuestionBoardContext, type JobTemplateQuestionBoardParams } from './job.template.question.model';

/**
 * The same `jobtemplates` documents as Job Template at its own metadata chain, so the board inherits
 * none of its columns, filters or grouping. Create and edit share one dialog — a question is the same
 * shape either way, and only the saving command differs.
 */
/**
 * Wide enough for two master panes beside the properties pane, which is what the layout needs.
 * `chrome: 'none'` because the host titles a create dialog from the entity alone, and all five Add
 * commands share it — only the editor knows which type it was opened for, and whether it is editing.
 */
const QUESTION_EDITOR_PRESENTATION = { mode: 'dialog', width: 'min(1600px, calc(100vw - 48px))', height: 'min(860px, calc(100vh - 48px))', chrome: 'none' } as const;

/**
 * Lex reorder on the table. Generic patch is closed on this entity — drag writes PatchQuestion
 * with `sortOrder` only, then refreshes the board and the host template.
 */
const jobTemplateQuestionReorder: EntityBoardReorderConfig<JobTemplateQuestion, JobTemplateQuestionBoardParams> = {
  field: 'sortOrder',
  displays: [EntityViewDisplay.Table],
  handle: true,
  withinGroupOnly: true,
  canReorder: ({ context }) => resolveBoardParams<JobTemplateQuestionBoardParams>(context)?.writable() === true,
  onReorder: async ({ item, sortOrder, field, context }) => {
    const questionId = resolveBoardRowId(item, [JOB_TEMPLATE_QUESTION_SCOPE]);
    if (!questionId || questionId === 'row:null' || questionId.startsWith('leaf-fallback:')) return;

    const entityData = inject(EntityDataService);
    await entityData.executeCommand(ActivityEntityName.JobTemplateQuestions, JobTemplateQuestionCommandId.PatchQuestion, { questionId, patch: { [field]: sortOrder } });
    await entityData.invalidateItems(ActivityEntityName.JobTemplateQuestions);
    resolveBoardParams<JobTemplateQuestionBoardParams>(context)?.questionsChanged?.();
  },
};

const loadQuestionEditor = () => import('./dialogs/question.editor').then(module => module.JobTemplateQuestionEditorComponent);

export const jobTemplateQuestionManifest = registerManifest<JobTemplateQuestion>({
  entity: ActivityEntityName.JobTemplateQuestions,
  slug: 'job-template-question',

  operations: {
    /**
     * Custom, not a Surface Engine form: the editor is a master/detail and the engine's repeat has no
     * such layout. It also sidesteps the record load — the strategy has no per-question read, so the
     * editor reads its row out of the template on `params`.
     */
    create: { kind: 'custom', load: loadQuestionEditor, presentation: QUESTION_EDITOR_PRESENTATION },
    edit: { kind: 'custom', load: loadQuestionEditor, presentation: QUESTION_EDITOR_PRESENTATION },
  },

  board: {
    // Table only: the authored order is the point, and neither cards nor lanes carry it.
    supportedDisplays: [EntityViewDisplay.Table],

    defaults: {
      table: {
        overrideCellRenderer: overrideJobTemplateQuestionCellRenderer,
      },
      header: {
        title: 'Questions',
        // Same as Tasks: the tab is already named, but the board header still needs a title once
        // the view chrome is off, or the left side is only the search box.
        showTitle: true,
        showSearch: true,
        showPaginator: false,
        // One authored list, not a switchable view — the label and star would be dead chrome.
        showViewOptions: false,
        // Standalone mounts stay read-only; the Job Template manage tab turns Add on.
        hideAdd: true,
      },
      restrictions: {
        recordScopes: [JOB_TEMPLATE_QUESTION_SCOPE],
      },
      itemInteraction: {
        commandsDisplay: 'menu',
        commands: (row, context) => buildJobTemplateQuestionRowCommands(row, resolveBoardParams<JobTemplateQuestionBoardParams>(context as JobTemplateQuestionBoardContext)),
      },
      reorder: jobTemplateQuestionReorder,
    },
  },
});

/**
 * `showCondition` is an embedded object, so the framework renderer has nothing to print. The cell reads
 * Always / Enabled and is the only place the rule is opened from, as on the legacy grid.
 */
function overrideJobTemplateQuestionCellRenderer(context: EntityBoardContext<JobTemplateQuestion>, _metadata: MetadataEntityData, field: MetadataEntityFieldData, renderer: unknown): unknown {

  if (field.name !== 'showCondition') return renderer;

  return {
    ...(renderer as object),
    cellRenderer: QuestionShowConditionCellRenderer,
    cellRendererParams: {
      ...(renderer as { cellRendererParams?: object })?.cellRendererParams,
      boardParams: resolveBoardParams<JobTemplateQuestionBoardParams>(context),
    },
  };
}
